import { loadScript, waitFor } from './loadScript';
import { isHeicName, type PickedFile } from './types';

/* Minimal shapes for the two Google SDKs we touch. */
interface GapiSdk {
  load(api: string, callback: () => void): void;
}

interface PickerDocument {
  id: string;
  name: string;
  sizeBytes?: string | number;
  mimeType?: string;
}

interface PickerResponse {
  action: string;
  docs?: PickerDocument[];
}

/** Every PickerBuilder setter returns the builder, so the calls chain. */
interface PickerBuilder {
  addView(view: unknown): PickerBuilder;
  setOAuthToken(token: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setAppId(appId: string): PickerBuilder;
  setTitle(title: string): PickerBuilder;
  enableFeature(feature: string): PickerBuilder;
  setCallback(cb: (data: PickerResponse) => void): PickerBuilder;
  build(): { setVisible(visible: boolean): void };
}

interface GoogleSdk {
  picker: {
    Action: { PICKED: string; CANCEL: string };
    DocsView: new (viewId?: string) => {
      setIncludeFolders(v: boolean): unknown;
      setSelectFolderEnabled(v: boolean): unknown;
      setMimeTypes(v: string): unknown;
    };
    ViewId: { DOCS_IMAGES: string; DOCS: string };
    PickerBuilder: new () => PickerBuilder;
    Feature: { MULTISELECT_ENABLED: string };
  };
  accounts: {
    oauth2: {
      initTokenClient(options: {
        client_id: string;
        scope: string;
        callback: (response: { access_token?: string; error?: string }) => void;
      }): { requestAccessToken(overrides?: { prompt?: string }): void };
    };
  };
}

declare global {
  interface Window {
    gapi?: GapiSdk;
    google?: GoogleSdk;
  }
}

const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
const apiKey = import.meta.env.VITE_GOOGLE_API_KEY as string | undefined;

export const googleDriveConfigured = Boolean(clientId && apiKey);

/**
 * The Picker needs the Cloud project number before it will grant us the files
 * somebody picks. That number is the prefix of the OAuth client id, so reusing
 * the login client leaves nothing extra to configure.
 */
const appId = clientId?.split('-')[0] ?? '';

/**
 * Per-file access: the Picker hands us only what the person selects. The broad
 * `drive.readonly` scope reads their whole Drive, which Google classes as a
 * restricted scope and will not approve without a verification review.
 */
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

let cachedToken: string | null = null;

async function getAccessToken(): Promise<string> {
  if (cachedToken) return cachedToken;

  await loadScript('https://accounts.google.com/gsi/client');
  const google = await waitFor(() => window.google?.accounts?.oauth2 && window.google);

  return new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId as string,
      scope: DRIVE_SCOPE,
      callback: (response) => {
        if (response.access_token) {
          cachedToken = response.access_token;
          // Google's implicit tokens last an hour; drop ours a little early.
          setTimeout(() => (cachedToken = null), 50 * 60 * 1000);
          resolve(response.access_token);
        } else {
          reject(new Error(response.error || 'Google sign-in was cancelled'));
        }
      },
    });
    client.requestAccessToken();
  });
}

/**
 * Google Drive Picker. Drive has no pre-signed download URLs, so the access
 * token travels with the request and the server calls the Drive API with it.
 */
export async function pickFromGoogleDrive(): Promise<PickedFile[]> {
  if (!googleDriveConfigured) throw new Error('Google Drive is not configured on this deployment');

  const token = await getAccessToken();

  await loadScript('https://apis.google.com/js/api.js');
  const gapi = await waitFor(() => window.gapi);
  await new Promise<void>((resolve) => gapi.load('picker', resolve));
  const google = await waitFor(() => (window.google?.picker ? window.google : undefined));

  return new Promise((resolve, reject) => {
    try {
      const view = new google.picker.DocsView(google.picker.ViewId.DOCS_IMAGES);
      view.setIncludeFolders(true);
      view.setSelectFolderEnabled(false);
      view.setMimeTypes('image/heic,image/heif');

      new google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(token)
        .setDeveloperKey(apiKey as string)
        .setAppId(appId)
        .setTitle('Select HEIC photos')
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setCallback((data) => {
          if (data.action === google.picker.Action.CANCEL) return resolve([]);
          if (data.action !== google.picker.Action.PICKED) return;

          const docs = data.docs ?? [];
          resolve(
            docs
              .filter((doc) => isHeicName(doc.name) || doc.mimeType === 'image/heic' || doc.mimeType === 'image/heif')
              .map((doc) => ({
                name: doc.name,
                url: 'https://www.googleapis.com/drive/v3/files/' + encodeURIComponent(doc.id) + '?alt=media',
                bytes: doc.sizeBytes ? Number(doc.sizeBytes) : undefined,
                bearerToken: token,
                source: 'google-drive' as const,
              })),
          );
        })
        .build()
        .setVisible(true);
    } catch (err) {
      reject(err instanceof Error ? err : new Error('Could not open the Google Drive picker'));
    }
  });
}
